<template>
    <div class="form-group" data-validation="required">
        <label for="day">{{ title }}</label>

        <div class="mb-2" v-if="invitation">
            Préférence : {{ invitation.preference_message }}
        </div>

        <div v-if="dayTimeRangesGrouped.length === 0" class="alert message__warning">
            Aucune plage horaire disponible.
        </div>
        <div class="row no-gutters">
            <div v-for="(timeRanges, day) in dayTimeRangesGrouped" :key="day" class="mb-3">
                <h5>{{ formatLongDate(day) }}</h5>
                <div class="row no-gutters">
                    <label
                        v-for="(timeRange, index) in timeRanges"
                        :key="`${day}-${index}`"
                        class="col-md-4 choiceButton"
                        :class="{
                                    'unavailable-selected': isSelectedAndUnavailable(day, timeRange),
                                    'booked-by-student': isSlotBookedByStudent(day, timeRange.start_time, timeRange.end_time)
                        }"
                    >
                        <input
                            type="radio"
                            required
                            :disabled="!timeRange.available || isSlotBookedByStudent(day, timeRange.start_time, timeRange.end_time)"
                            :name="inputName"
                            :value="formatDateTime(day, timeRange.start_time)"
                            :checked="invitation && invitation.preference_message === formatDateTime(day, timeRange.start_time)"
                        >
                        <span class="choiceButton__label">
                            {{ formatTime(timeRange.start_time) }} - {{ formatTime(timeRange.end_time) }}
                        </span>
                    </label>
                </div>

                <!-- Message displayed if the selected time for this day is unavailable -->
                <div v-if="isSelectedDayUnavailable(day)" class="text-danger mt-1">
                    {{ slotAlreadySelected }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AppointmentTimeSelector',
    props: {
        dayTimeRangesGrouped: {
            type: Object,
            required: true
        },
        invitation: {
            type: Object,
            default: null
        },
        company: {
            type: Object,
            default: null
        },
        studentProfile: {
            type: Object,
            default: null
        },
        inputName: {
            type: String,
            default: 'preference'
        },
        title: String,
        slotAlreadySelected: String,
    },
    data() {
        return {
            unavailableTimeSlots: []
        };
    },
    watch: {
        invitation: {
            deep: true,
            handler(newValue) {
                if (newValue && newValue.inviter && newValue.inviter.student_profile_id && this.company && this.company.id) {
                    this.fetchUnavailableSlots(newValue.inviter.student_profile_id);
                }
            }
        },
        studentProfile: {
            deep: true,
            immediate: true,
            handler(newValue) {
                console.log('studentProfile changed:', newValue);
                if (newValue && newValue.id && this.company && this.company.id) {
                    this.fetchUnavailableSlots(newValue.id);
                }
            }
        }
    },
    methods: {
        formatLongDate(day) {
            return new Date(day).toLocaleDateString('fr-CH', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        formatDateTime(day, time) {
            const date = new Date(`${day}T${time}`);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const dayStr = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${year}-${month}-${dayStr} ${hours}:${minutes}:${seconds}`;
        },
        formatTime(time) {
            const [hours, minutes] = time.split(':');

            return `${hours}:${minutes}`;
        },
        isSlotBookedByStudent(day, startTime, endTime) {
            if (!this.unavailableTimeSlots || !this.unavailableTimeSlots.length) {
                return false;
            }

            const currentSlotStart = this.formatDateTime(day, startTime);
            const currentSlotEnd = this.formatDateTime(day, endTime);

            return this.unavailableTimeSlots.some(slot => {
                const unavailableStart = this.formatDateTime(slot.day, slot.start);
                const unavailableEnd = this.formatDateTime(slot.day, slot.end);

                return currentSlotStart < unavailableEnd && currentSlotEnd > unavailableStart;
            });
        },
        isSelectedAndUnavailable(day, timeRange) {
            const dateTime = this.formatDateTime(day, timeRange.start_time);
            return this.invitation &&
                this.invitation.preference_message === dateTime &&
                (!timeRange.available || this.isSlotBookedByStudent(dateTime));
        },
        isSelectedDayUnavailable(day) {
            if (!this.invitation || !this.invitation.preference_message) {
                return false;
            }

            if (!this.invitation.preference_message.startsWith(day)) {
                return false;
            }

            const selectedTimeRange = this.dayTimeRangesGrouped[day]?.find(timeRange =>
                this.invitation.preference_message === this.formatDateTime(day, timeRange.start_time)
            );

            return selectedTimeRange && (!selectedTimeRange.available || this.isSlotBookedByStudent(this.invitation.preference_message));
        },
        fetchUnavailableSlots(studentProfileId) {
            axios.get(`/company/${this.company.id}/lodges/unavailable-slots/${studentProfileId}`)
                .then(response => {
                    this.unavailableTimeSlots = response.data.unavailable_slots || [];
                })
                .catch(error => {
                    console.error('Error fetching unavailable slots:', error);
                    this.unavailableTimeSlots = [];
                });
        },
    }
}
</script>

<style scoped>
.unavailable-selected :checked + .choiceButton__label
{
    border-color: #C73466!important;
    background-color: #f8d7e1!important;
    color: #721c24!important;
}

.unavailable-selected :checked + .choiceButton__label:hover {
    border-color: #C73466;
    z-index: 10;
}

.booked-by-student input:disabled + .choiceButton__label {
    background-color: #f8dbc0;
    border-color: #f4caa1;
    color: #6c757d;
    opacity: 0.8;
}

input:disabled {
    cursor: not-allowed;
}

.text-danger {
    color: #C73466;
}
</style>
